import { useEffect, useRef, useState } from "react";
import { type GCircle, type GMap, type GMarker, type LatLngLiteral, loadGoogleMaps } from "../lib/maps";

/** Centre of England-ish, used until the site has a pin. */
const UK_CENTRE: LatLngLiteral = { lat: 52.8, lng: -1.6 };

/**
 * Google map with a draggable pin and the geofence circle. Clicking the map also moves the pin.
 * The parent owns the position; this component just mirrors it and reports moves.
 */
export function SiteMap({
  apiKey,
  position,
  radiusM,
  onMove,
}: {
  apiKey: string;
  position: LatLngLiteral | null;
  radiusM: number;
  onMove: (p: LatLngLiteral) => void;
}) {
  const el = useRef<HTMLDivElement>(null);
  const objs = useRef<{ map: GMap; marker: GMarker; circle: GCircle } | null>(null);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const [error, setError] = useState<string | null>(null);
  // Latest props, read when the (async) map is created; later changes go through the effects below.
  const latest = useRef({ position, radiusM });
  latest.current = { position, radiusM };

  useEffect(() => {
    let cancelled = false;
    const listeners: { remove(): void }[] = [];
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !el.current) return;
        const start = latest.current.position ?? UK_CENTRE;
        const map = new maps.Map(el.current, {
          center: start,
          zoom: latest.current.position ? 18 : 6,
          mapTypeControl: false,
          streetViewControl: false,
          clickableIcons: false,
          gestureHandling: "cooperative",
        });
        const marker = new maps.Marker({ position: start, map, draggable: true, title: "Site entrance" });
        const circle = new maps.Circle({
          center: start,
          radius: latest.current.radiusM,
          map,
          clickable: false,
          strokeColor: "#19525d",
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: "#4cc3c9",
          fillOpacity: 0.15,
        });
        listeners.push(
          marker.addListener("drag", () => {
            const p = marker.getPosition();
            if (p) circle.setCenter({ lat: p.lat(), lng: p.lng() });
          }),
          marker.addListener("dragend", () => {
            const p = marker.getPosition();
            if (p) onMoveRef.current({ lat: p.lat(), lng: p.lng() });
          }),
          map.addListener("click", (e) => {
            if (e.latLng) onMoveRef.current({ lat: e.latLng.lat(), lng: e.latLng.lng() });
          }),
        );
        objs.current = { map, marker, circle };
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
      for (const l of listeners) l.remove();
      objs.current?.marker.setMap(null);
      objs.current?.circle.setMap(null);
      objs.current = null;
    };
  }, [apiKey]);

  // Keep the pin and circle in step with the form (postcode lookup, typed radius, drags).
  const lat = position?.lat;
  const lng = position?.lng;
  useEffect(() => {
    const o = objs.current;
    if (!o || lat == null || lng == null) return;
    const p = { lat, lng };
    const pos = o.marker.getPosition();
    const moved = !pos || Math.abs(pos.lat() - lat) > 1e-7 || Math.abs(pos.lng() - lng) > 1e-7;
    o.marker.setPosition(p);
    o.circle.setCenter(p);
    if (moved) {
      o.map.panTo(p);
      if ((o.map.getZoom() ?? 0) < 16) o.map.setZoom(18);
    }
  }, [lat, lng]);

  useEffect(() => {
    objs.current?.circle.setRadius(radiusM);
  }, [radiusM]);

  if (error) return <div className="banner danger">{error}</div>;
  return (
    <div ref={el} className="adm-map" role="application" aria-label="Map: drag the pin to the entrance" />
  );
}
